<script setup lang="ts">
import type { ExtendedAlgoliaOffer } from '@global-types/algoliaOffer';
import OfferCard from '@components/OfferCard/OfferCard.vue';
import FavoriteButton from '@components/FavoriteButton.vue';
import { computed } from 'vue';
import { useSearchStore } from '@stores/useSearchStore';
import { storeToRefs } from 'pinia';
import { PriceTypeMode } from '@enums/priceTypeMode.ts';
import { type DeserializedOffer, getDeserializedOffer } from '@pages/search/serializer/deserializedOffer';
import PromoteType from '@components/OfferCard/PromoteType.vue';
import OfferPrice from '@components/OfferCard/OfferPrice.vue';
import Typography from '@components/Typography.vue';
import StreetDistrict from '@components/OfferCard/StreetDistrict.vue';
import Transports from '@components/OfferCard/Transports.vue';
import { trans } from '@tools/translation.ts';
import { isOfferRentWorkstation } from '@organisms/offer/offer-utils';

const { offer } = defineProps<{ offer: ExtendedAlgoliaOffer }>();
const searchStore = useSearchStore();

const {
  addFavorite,
  removeFavorite,
  isFavorite,
  setHoverOfferHashId,
  getAggregatedOfferUrl,
} = searchStore;
const { hoverOfferHashId } = storeToRefs(searchStore);

const isGroupedOffer = computed<boolean>(() => (offer.grouped_offers?.length ?? 0) > 0);

const deserializedOffer = computed<DeserializedOffer>(() => getDeserializedOffer(offer));

const isHighlighted = computed<boolean>(() => offer.hash_id === hoverOfferHashId.value);

const onFavoriteChange = async (value: boolean): Promise<void> => {
  if (value) {
    addFavorite(offer.hash_id);
    return;
  }

  removeFavorite(offer.hash_id);
};

const capacityRange = computed<{ min: number, max: number }>(() => {
  const mergedOffers = isGroupedOffer.value && offer.grouped_offers
    ? [ offer, ...offer.grouped_offers ]
    : [ offer ];

  const capacities = mergedOffers.map((mergedOffer) => mergedOffer.capacity);

  return {
    min: isOfferRentWorkstation(offer) ? 1 : Math.min(...capacities),
    max: Math.max(...capacities),
  };
});
</script>

<template>
  <OfferCard
    :class="[
      'search-offer-card',
      { 'search-offer-card--hover-highlighted': isHighlighted },
    ]"
    :first-offer="deserializedOffer"
    :offers="deserializedOffer.aggregated_offers"
    :url="getAggregatedOfferUrl(deserializedOffer)"
    :price-suffix="PriceTypeMode.FULL"
    :width="581"
    :image-ratio="[ 1, 1 ]"
    :with-tax-unit="false"
    @mouseenter="setHoverOfferHashId(offer.hash_id)"
    @mouseleave="setHoverOfferHashId(null)"
  >
    <template #actions>
      <FavoriteButton
        :offer-hash-id="offer.hash_id"
        :is-active="isFavorite(offer.hash_id)"
        @change="onFavoriteChange"
      />
    </template>

    <template v-if="isGroupedOffer" #body>
      <div
        :class="[
          'relative',
          'flex flex-col',
          'p-3',
        ]"
      >
        <PromoteType
          v-if="offer.promote_types.length"
          class="z-[1]"
          :promote-type="offer.promote_types[0].code"
        />

        <div class="flex gap-x-2">
          <StreetDistrict :address="offer.address" />

          <Transports
            v-if="offer.address.first_station"
            :lines="offer.address.first_station.unique_lines"
          />
        </div>

        <Typography
          variant="body"
          color="gray-darker"
        >
          {{ deserializedOffer.type_infos }}
        </Typography>

        <Typography
          variant="body"
          color="gray-darker"
        >
          {{ capacityRange.min }} {{ trans('global.at') }} {{ capacityRange.max }} {{ trans('global.capacity_unit') }}

          <template v-if="!isOfferRentWorkstation(offer)">
            <!-- @todo : adapt to the right rule, currently iso prod, @see https://bureaux-a-partager.atlassian.net/browse/MATCH-10794 -->
            • {{ offer.surface }} {{ trans('global.meter.square') }}
          </template>
        </Typography>

        <div>
          <Typography
            variant="caption"
            color="gray-darker"
            class="!-mb-1"
          >
            {{ trans('global.from') }}
          </Typography>

          <OfferPrice
            :offer="deserializedOffer"
            :suffix="PriceTypeMode.FULL"
            :price="deserializedOffer.monthly_total_price"
            :with-tax-unit="false"
          />
        </div>
      </div>
    </template>
  </OfferCard>
</template>

<style lang="scss" scoped>
@use '@styles/mixins' as *;

.search-offer-card {
  @apply m-0 min-w-[250px];

  &--hover-highlighted {
    box-shadow: 0 toRem(4) toRem(8) rgb(0 0 0 / 0.25);

    @apply bg-gray-lighter;
  }
}

:deep(.offer-card__slider img) {
  @apply h-[230px] w-full;

  @apply object-cover;
}
</style>
